import React, { createContext, useContext, useState, useCallback, PropsWithChildren, useRef, useEffect, useMemo } from 'react';
import { AppContext } from 'Context/AppContext';
import useDebounce from '../hooks/useDebounce';
import { fetchCategoriesAndCategoryTypes, fetchCoSoldCategories, getComputeStatus, toggleCuration, updateReviewStatus, triggerFullCompute } from '../services/CategoryRelevancyServices';
import { toast } from 'Components/DesignSystem/Toast/useToast';

export type CategoryRelevancyContextType = {
    activeTab: TabType;
    masterCategories: Category[];
    masterCategoryTypes: CategoryType[];
    rowDensity: RowDensity;
    industryId: string | null | undefined;
    openMenu: "filter" | "topItems" | "settings" | "analysis" | undefined;
    isComputing: boolean;
    computeStartedAt: number | null;
    handleComputeTrigger: () => Promise<void>;
    notifyWeightageRescoreTriggered: () => void;
    selectedAnalyses: AnalysisOption[];
    isUnifiedAnalysis: boolean;
    top25SelectedAnalyses: AnalysisOption[];
    top25IsUnifiedAnalysis: boolean;
    top25Rows: CoSoldCategoryParent[];
    top25Loading: boolean;
    top25SortBy: SortOption;
    top25SortOrder: SortOrder;
    top25SearchTerm: string;
    /**
     * § 288 — the typed term has outrun the debounced one the fetch is keyed on,
     * so the rows on screen answer an older term. Derived, never stored; the
     * loading flags above start with the REQUEST, which is 500ms too late to be
     * the whole wait.
     */
    top25SearchPending: boolean;
    top25Filters: FilterOptions;
    handleTop25Search: (term: string) => void;
    handleTop25AnalysisChange: (option: AnalysisOption) => void;
    handleTop25Sort: (column: SortOption) => void;
    handleTop25FilterChange: (newFilters: FilterOptions) => void;
    handleTop25RemoveFilter: (filterType: keyof FilterOptions | 'all', value?: string) => void;
    showTabSelector?: boolean;

    lastUpdatedOn: number | null;
    readPermission: boolean | undefined;
    updatePermission: boolean | undefined;
    handleUpdateReviewStatus: (coSoldCategoryId: string, parentCategoryId: string) => Promise<void>;
    rawCategories: Category[] | undefined;
    rawCategoryTypes: CategoryType[];
    rawExpandedCategories: Set<string>;
    rawCoSoldCategories: CoSoldCategoryParent[];
    rawFilters: FilterOptions;
    topItems: TopItemsOption;
    customTopItems: number;
    rawSearchTerm: string;
    /** § 288 — see `top25SearchPending`. */
    rawSearchPending: boolean;
    rawSortBy: SortOption;
    rawSortOrder: SortOrder;
    rawLoadingCategories: boolean;
    isRawCoSoldCategoryLoading: (categoryId: string) => boolean

    curatedCategories: Category[] | undefined;
    curatedExpandedCategories: Set<string>;
    curatedCoSoldCategories: CoSoldCategoryParent[];
    curatedFilters: FilterOptions;
    curatedSearchTerm: string;
    /** § 288 — see `top25SearchPending`. */
    curatedSearchPending: boolean;
    curatedSortBy: SortOption;
    curatedSortOrder: SortOrder;
    curatedLoadingCategories: boolean;
    isCuratedCoSoldCategoryLoading: (categoryId: string) => boolean

    setActiveTab: (tab: TabType) => void;
    handleRowDensityChange: (density: RowDensity) => void;
    setOpenMenu: React.Dispatch<React.SetStateAction<"filter" | "topItems" | "settings" | "analysis" | undefined>>;
    handleAnalysisChange: (option: AnalysisOption) => void;

    handleRawCategoryExpand: (categoryId: string) => Promise<void>;
    handleToggleCuration: (coSoldCategoryId: string, parentCategoryId: string, isCurated: boolean, reviewStatus: CoSoldCategory['review_status']) => Promise<void>;
    handleRawSearch: (term: string) => void;
    handleRawFilterChange: (newFilters: FilterOptions) => void;
    handleTopItemsChange: (option: TopItemsOption, customValue?: number) => void;
    handleRawRemoveFilter: (filterType: keyof FilterOptions | 'all', value?: string) => void;
    handleRawSort: (column: SortOption) => void;
    getRawCoSoldCategoriesForId: (categoryId: string) => CoSoldCategory[];

    handleCuratedCategoryExpand: (categoryId: string) => Promise<void>;
    handleCuratedSearch: (term: string) => void;
    handleCuratedFilterChange: (newFilters: FilterOptions) => void;
    handleCuratedRemoveFilter: (filterType: keyof FilterOptions | 'all', value?: string) => void;
    handleCuratedSort: (column: SortOption) => void;
    getCuratedCoSoldCategoriesForId: (categoryId: string) => CoSoldCategory[];
}

export const CategoryRelevancyContext = createContext<CategoryRelevancyContextType>({} as CategoryRelevancyContextType);

type CategoryRelevancyProviderProps = PropsWithChildren<{
    initialTab?: TabType;
    showTabSelector?: boolean;
}>;

export const CategoryRelevancyProvider = ({
    children,
    initialTab = 'all',
    showTabSelector = true
}: CategoryRelevancyProviderProps) => {
    const [activeTab, setActiveTab] = useState<TabType>(initialTab);
    const [masterCategories, setMasterCategories] = useState<Category[]>([]);
    const [masterCategoryTypes, setMasterCategoryTypes] = useState<CategoryType[]>([]);
    const [rowDensity, setRowDensity] = useState<RowDensity>('normal');
    const [openMenu, setOpenMenu] = useState<'filter' | 'topItems' | 'settings' | 'analysis' | undefined>(undefined);
    // Single source of truth for the analysis (data_source) filter across All
    // and Top-25 tabs. Unified is a real, exclusive option — selecting it
    // restricts the BE filter to unified pairs only; selecting Order Data or
    // Taxonomy implicitly includes unified rows too (so the BE filter becomes
    // `unified,order_data` or `unified,taxonomy`). Mutex is enforced in
    // handleAnalysisChange so the array is always either ['unified'] or a
    // non-empty subset of ['order_data', 'taxonomy'].
    //
    // Default: Order Data + Taxonomy both checked (and Unified unchecked) so
    // the user sees every bucket on page load — the BE filter becomes
    // `unified,order_data,taxonomy`, i.e. all rows.
    const [selectedAnalyses, setSelectedAnalyses] = useState<AnalysisOption[]>(['order_data', 'taxonomy']);
    const isUnifiedAnalysis = selectedAnalyses.includes('unified');
    const [top25SelectedAnalyses, setTop25SelectedAnalyses] = useState<AnalysisOption[]>(['order_data', 'taxonomy']);
    const top25IsUnifiedAnalysis = top25SelectedAnalyses.includes('unified');
    const [top25Rows, setTop25Rows] = useState<CoSoldCategoryParent[]>([]);
    const [top25Loading, setTop25Loading] = useState<boolean>(false);
    const [top25SortBy, setTop25SortBy] = useState<SortOption>('invoice_count');
    const [top25SortOrder, setTop25SortOrder] = useState<SortOrder>(-1);
    const [top25SearchTerm, setTop25SearchTerm] = useState<string>('');
    const [top25Filters, setTop25Filters] = useState<FilterOptions>({
        categories: [],
        category_types: [],
        co_sold_categories: [],
        co_sold_category_types: [],
    });
    const [lastUpdatedOn, setLastUpdatedOn] = useState<number | null>(null);
    const [isComputing, setIsComputing] = useState<boolean>(false);
    const [computeStartedAt, setComputeStartedAt] = useState<number | null>(null);
    const prevIsComputingRef = useRef(false);
    // Last observed `last_computed_at` from the status poll. Used to detect
    // compute completion between two polls (fast computes like weight_rescore,
    // or pages loaded just after another session triggered a compute) — the
    // old isComputing-transition check silently missed these.
    const lastComputedAtRef = useRef<number | null>(null);
    // Counter bumped by `checkComputeStatus` whenever a compute is detected
    // to have completed. A separate effect watches it and refetches the
    // tables. Decoupled from checkComputeStatus's deps so the polling
    // interval doesn't re-arm whenever the fetch helpers' identities change.
    const [postComputeRefetchTrigger, setPostComputeRefetchTrigger] = useState(0);
    // Timestamp set when the user triggers a compute from the UI (manual
    // Compute button, or a weightage save that fires the BE's weight_rescore
    // Celery trigger). Because Celery → ARS handoff takes a few seconds,
    // the very first poll after a user-initiated trigger can still see
    // `status="Computed"` and would otherwise flip isComputing back to false
    // — collapsing the polling cadence back to 10 min and effectively
    // hiding the rescore from the user. While this ref is set and within
    // the sticky window, `checkComputeStatus` holds the optimistic
    // isComputing=true state. The flag clears when ARS confirms `Computing`,
    // when `last_computed_at` advances (rescore completed), or when the
    // sticky window expires.
    const userInitiatedComputeAtRef = useRef<number | null>(null);
    const USER_INITIATED_COMPUTE_STICKY_MS = 60_000;

    const [rawCategories, setRawCategories] = useState<Category[] | undefined>(undefined);
    const [rawCategoryTypes, setRawCategoryTypes] = useState<CategoryType[]>([]);
    const [rawExpandedCategories, setRawExpandedCategories] = useState<Set<string>>(new Set());
    const [rawCoSoldCategories, setRawCoSoldCategories] = useState<CoSoldCategoryParent[]>([]);
    const [rawSortBy, setRawSortBy] = useState<SortOption>('category_name');
    const [rawSortOrder, setRawSortOrder] = useState<SortOrder>(1);
    const [rawFilters, setRawFilters] = useState<FilterOptions>({
        categories: [],
        category_types: [],
        co_sold_categories: [],
        co_sold_category_types: [],
    });
    const [topItems, setTopItems] = useState<TopItemsOption>(25);
    const [customTopItems, setCustomTopItems] = useState<number>(25);
    const [rawSearchTerm, setRawSearchTerm] = useState<string>('');
    const [rawLoadingCategories, setRawLoadingCategories] = useState<boolean>(false);
    const [rawLoadingCoSoldCategoryIds, setRawLoadingCoSoldCategoryIds] = useState<string[]>([]);

    const [curatedCategories, setCuratedCategories] = useState<Category[] | undefined>(undefined);
    const [curatedExpandedCategories, setCuratedExpandedCategories] = useState<Set<string>>(new Set());
    const [curatedCoSoldCategories, setCuratedCoSoldCategories] = useState<CoSoldCategoryParent[]>([]);
    const [curatedSortBy, setCuratedSortBy] = useState<SortOption>('category_name');
    const [curatedSortOrder, setCuratedSortOrder] = useState<SortOrder>(1);
    const [curatedFilters, setCuratedFilters] = useState<FilterOptions>({
        categories: [],
        category_types: [],
        co_sold_categories: [],
        co_sold_category_types: [],
    });
    const [curatedSearchTerm, setCuratedSearchTerm] = useState<string>('');
    const [curatedLoadingCategories, setCuratedLoadingCategories] = useState<boolean>(false);
    const [curatedLoadingCoSoldCategoryIds, setCuratedLoadingCoSoldCategoryIds] = useState<string[]>([]);

    const masterListsLoaded = useRef(false);
    const rawInitialized = useRef(false);
    const curatedInitialized = useRef(false);
    const top25Initialized = useRef(false);
    const analysisEffectMounted = useRef(false);

    const debouncedRawSearchTerm = useDebounce(rawSearchTerm, 500);
    const debouncedCuratedSearchTerm = useDebounce(curatedSearchTerm, 500);
    const debouncedTop25SearchTerm = useDebounce(top25SearchTerm, 500);

    /* § 288 — one boolean per tab: what was typed vs what the fetch has been given.
       `useDebounce` passes the value through untouched, so these converge exactly
       when the term commits — no trim to account for. */
    const rawSearchPending = rawSearchTerm !== debouncedRawSearchTerm;
    const curatedSearchPending = curatedSearchTerm !== debouncedCuratedSearchTerm;
    const top25SearchPending = top25SearchTerm !== debouncedTop25SearchTerm;

    const rawFetchParamsRef = useRef(new Map<string, {
        filters: FilterOptions;
        topItems: TopItemsOption;
        customTopItems: number;
        sortBy: SortOption;
        sortOrder: SortOrder;
        searchTerm: string;
    }>());

    const curatedFetchParamsRef = useRef(new Map<string, {
        filters: FilterOptions;
        sortBy: SortOption;
        sortOrder: SortOrder;
        searchTerm: string;
    }>());

    const appContext = useContext(AppContext);
    const industryId = appContext && appContext?.get_selected_industry() && appContext?.get_selected_industry()?.id;
    const channelKey = appContext?.channelKey;
    const readPermission = appContext.pm.checkPermission("gmscategoryrelevancy_read");
    const updatePermission = appContext.pm.checkPermission("gmscategoryrelevancy_update");

    // Unified-only → 'unified'; selecting Order Data or Taxonomy implicitly
    // includes unified rows ('unified,order_data' / 'unified,taxonomy' / all
    // three). Always returns a non-empty value because the selection state is
    // never empty (see handleAnalysisChange).
    const buildDataSource = useCallback((analyses: AnalysisOption[]): string | undefined => {
        if (analyses.length === 0) return undefined;
        if (analyses.includes('unified')) return 'unified';
        return ['unified', ...analyses].join(',');
    }, []);

    // Mirror of `_default_sort_for_data_source` on the BE. Used only for the
    // Top-25 flat-pair tab, where 5958 AC#3 mandates sort-by-natural-metric
    // (invoice_count for Order Data, attribute_overlap for Taxonomy,
    // unified_score for Unified / both buckets). The ALL and Curated tabs
    // intentionally keep their parent-row sort on `category_name` (parent
    // docs don't carry score fields, so any score-field sort would silently
    // no-op anyway — see `Docs/api/category_relevancy/category-relevancy-list-and-co-sold.md`).
    const naturalSortForAnalyses = useCallback((analyses: AnalysisOption[]): { sortBy: SortOption; sortOrder: SortOrder } => {
        const isUnifiedOnly = analyses.length === 1 && analyses[0] === 'unified';
        const isOrderOnly = analyses.length === 1 && analyses[0] === 'order_data';
        const isTaxonomyOnly = analyses.length === 1 && analyses[0] === 'taxonomy';
        if (isOrderOnly) return { sortBy: 'invoice_count', sortOrder: -1 };
        if (isTaxonomyOnly) return { sortBy: 'attribute_overlap', sortOrder: -1 };
        // unified-only or both-buckets (`['order_data','taxonomy']`) — fall
        // back to the BE's cascade primary, `unified_score`.
        if (isUnifiedOnly) return { sortBy: 'unified_score', sortOrder: -1 };
        return { sortBy: 'unified_score', sortOrder: -1 };
    }, []);

    const handleAnalysisChange = useCallback((option: AnalysisOption) => {
        setSelectedAnalyses(prev => {
            let next: AnalysisOption[];
            if (option === 'unified') {
                // Toggle unified: checking it → exclusive; unchecking it →
                // default to both data sources (never leave the array empty).
                next = prev.includes('unified') ? ['order_data', 'taxonomy'] : ['unified'];
            } else if (prev.includes('unified')) {
                // option is 'order_data' or 'taxonomy'. Checking a data-
                // source option while unified was on → drop unified, set
                // just this option.
                next = [option];
            } else if (prev.includes(option)) {
                // Unchecking this option. If it was the last one, auto-check
                // unified (never leave the array empty).
                const filtered = prev.filter(a => a !== option);
                next = filtered.length === 0 ? ['unified'] : filtered;
            } else {
                // Checking option (already have the other data-source).
                next = [...prev, option];
            }
            return next;
        });
    }, []);

    const handleTop25Search = useCallback((term: string) => {
        setTop25SearchTerm(term);
    }, []);

    const handleTop25AnalysisChange = useCallback((option: AnalysisOption) => {
        setTop25SelectedAnalyses(prev => {
            let next: AnalysisOption[];
            if (option === 'unified') {
                next = prev.includes('unified') ? ['order_data', 'taxonomy'] : ['unified'];
            } else if (prev.includes('unified')) {
                next = [option];
            } else if (prev.includes(option)) {
                const filtered = prev.filter(a => a !== option);
                next = filtered.length === 0 ? ['unified'] : filtered;
            } else {
                next = [...prev, option];
            }
            const { sortBy, sortOrder } = naturalSortForAnalyses(next);
            setTop25SortBy(sortBy);
            setTop25SortOrder(sortOrder);
            return next;
        });
    }, [naturalSortForAnalyses]);

    const fetchTop25Data = useCallback(async (
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearch?: string,
        explicitFilters?: FilterOptions
    ) => {
        if (!channelKey) return;

        setTop25Loading(true);

        const sortColumn = explicitSortBy !== undefined ? explicitSortBy : top25SortBy;
        const sortDirection = explicitSortOrder !== undefined ? explicitSortOrder : top25SortOrder;
        const searchTerm = explicitSearch !== undefined ? explicitSearch : debouncedTop25SearchTerm;
        const filtersToUse = explicitFilters !== undefined ? explicitFilters : top25Filters;

        try {
            // Top-25 mode on the BE picks the top 25 by the cascade
            // (is_curated DESC, unified_score DESC, invoice_count DESC,
            // attribute_overlap DESC). The user's column-header click is the
            // *display* sort over those 25, so it goes to sort_by/sort_order.
            // Don't send co_sort_by/co_sort_order — letting the BE default
            // means the selection cascade applies.
            const data = await fetchCategoriesAndCategoryTypes({
                channelKey,
                sort_by: sortColumn,
                sort_order: sortDirection,
                limit: 25,
                search: searchTerm,
                data_source: buildDataSource(top25SelectedAnalyses),
                filters: filtersToUse,
            });

            // BE returns flat `pairs` already ordered by sort_by/sort_order. Wrap each
            // pair in its own CoSoldCategoryParent so Top25Table.flatMap preserves the
            // order. The previous parentMap grouping bucketed all pairs of the same
            // parent together (insertion order across parents), which destroyed the
            // BE's sort — e.g. with sort_by=attribute_overlap, the 71s would get pulled
            // up under the first parent that referenced them and the rest of the 71s
            // landed wherever their parent first appeared. Per spec story 5958 AC #5,
            // Top 25 is a flat list and the same parent can repeat.
            const pairsData: any[] = data?.data?.pairs || [];
            const flatRows: CoSoldCategoryParent[] = pairsData.map((pair) => ({
                category_id: pair.category_id,
                category_name: pair.category_name,
                category_type_id: pair.category_type_id,
                category_type_name: pair.category_type_name,
                co_sold_categories: [{
                    category_id: pair.co_sold_category_id,
                    category_name: pair.co_sold_category_name,
                    category_type_id: pair.co_sold_category_type_id,
                    category_type_name: pair.co_sold_category_type_name,
                    invoice_count: pair.invoice_count,
                    attribute_overlap: pair.attribute_overlap ?? null,
                    unified_score: pair.unified_score ?? null,
                    data_source: pair.data_source,
                    is_curated: pair.is_curated,
                    review_status: pair.review_status,
                }],
            }));
            setTop25Rows(flatRows);

            if (!masterListsLoaded.current && data?.data) {
                const uniqueMap = new Map<string, CategoryType>();
                (data.data.category_types || []).forEach((ct: CategoryType) => {
                    uniqueMap.set(ct.category_type_id, ct);
                });
                setMasterCategories((data.data.categories || []).map((c: Category) => ({ category_id: c.category_id, category_name: c.category_name })));
                setMasterCategoryTypes(Array.from(uniqueMap.values()));
                masterListsLoaded.current = true;
            }
        } catch (error) {
            console.error('Error fetching top 25 data:', error);
        } finally {
            setTop25Loading(false);
        }
    }, [channelKey, top25SortBy, top25SortOrder, debouncedTop25SearchTerm, buildDataSource, top25SelectedAnalyses, top25Filters]);

    const handleTop25Sort = useCallback((column: SortOption) => {
        const isSameColumn = column === top25SortBy;
        const newOrder: SortOrder = isSameColumn ? (top25SortOrder === 1 ? -1 : 1) : -1;

        fetchTop25Data(column, newOrder);
        setTop25SortBy(column);
        setTop25SortOrder(newOrder);
    }, [top25SortBy, top25SortOrder, fetchTop25Data]);

    const handleTop25FilterChange = useCallback((newFilters: FilterOptions) => {
        setTop25Filters(newFilters);
        fetchTop25Data(undefined, undefined, undefined, newFilters);
    }, [fetchTop25Data]);

    const handleTop25RemoveFilter = useCallback((filterType: keyof FilterOptions | 'all', value?: string) => {
        let newFilters: FilterOptions;

        if (filterType === 'all') {
            newFilters = {
                categories: [],
                category_types: [],
                co_sold_categories: [],
                co_sold_category_types: [],
                is_curated: undefined,
                curatedSelections: []
            };
        } else if (filterType === 'is_curated') {
            newFilters = { ...top25Filters, is_curated: undefined, curatedSelections: [] };
        } else if (value && Array.isArray(top25Filters[filterType])) {
            newFilters = { ...top25Filters };
            if (filterType === 'categories' || filterType === 'co_sold_categories') {
                newFilters[filterType] = top25Filters[filterType].filter((item: Category) => item.category_id !== value);
            }
            if (filterType === 'category_types' || filterType === 'co_sold_category_types') {
                newFilters[filterType] = top25Filters[filterType].filter((item: CategoryType) => item.category_type_id !== value);
            }
        } else {
            return;
        }

        handleTop25FilterChange(newFilters);
    }, [top25Filters, handleTop25FilterChange]);

    const loadMasterLists = useCallback(async () => {
        if (!channelKey || masterListsLoaded.current || !readPermission) return;

        try {
            const masterData = await fetchCategoriesAndCategoryTypes({
                search: '',
                channelKey: channelKey,
                sort_by: 'category_name',
                sort_order: 1
            });

            if (masterData?.data) {
                setMasterCategories(masterData.data.categories);
                setMasterCategoryTypesUnique(masterData.data.category_types);
                masterListsLoaded.current = true;
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelKey]);

    const setMasterCategoryTypesUnique = (categoryTypes: CategoryType[]) => {
        const uniqueMap = new Map<string, CategoryType>();

        categoryTypes.forEach(category => {
            uniqueMap.set(category.category_type_id, category);
        });

        setMasterCategoryTypes(Array.from(uniqueMap.values()));
    };

    const fetchRawCategories = useCallback(async (
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearchTerm?: string
    ) => {
        if (!channelKey || !readPermission) return;

        try {
            setRawLoadingCategories(true);

            const filtersToUse = explicitFilters || rawFilters;
            const searchTerm = explicitSearchTerm !== undefined ? explicitSearchTerm : debouncedRawSearchTerm;

            // Parent-list endpoint only honors category_name / category_type_name
            // sorts (parent docs don't carry score / co-sold fields, so those
            // sorts would silently no-op). Score / co-sold column-header clicks
            // are routed to the inner co-sold drill-in sort instead.
            const isParentSort = (col?: SortOption): col is SortOption => col === 'category_name' || col === 'category_type_name';
            const parentSortBy: SortOption = isParentSort(explicitSortBy) ? explicitSortBy : (isParentSort(rawSortBy) ? rawSortBy : 'category_name');
            const parentSortOrder: SortOrder = isParentSort(explicitSortBy) ? (explicitSortOrder ?? rawSortOrder) : (isParentSort(rawSortBy) ? rawSortOrder : 1);

            const data = await fetchCategoriesAndCategoryTypes({
                search: searchTerm,
                channelKey: channelKey,
                sort_by: parentSortBy,
                sort_order: parentSortOrder,
                filters: filtersToUse,
                data_source: buildDataSource(selectedAnalyses),
            });

            setRawCategories(data?.data?.categories || []);
            setRawCategoryTypes(data?.data?.category_types || []);

            if (!masterListsLoaded.current && data?.data) {
                const uniqueMap = new Map<string, CategoryType>();
                (data.data.category_types || []).forEach((ct: CategoryType) => {
                    uniqueMap.set(ct.category_type_id, ct);
                });
                setMasterCategories(data.data.categories || []);
                setMasterCategoryTypes(Array.from(uniqueMap.values()));
                masterListsLoaded.current = true;
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setRawLoadingCategories(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelKey, rawFilters, rawSortBy, rawSortOrder, debouncedRawSearchTerm, buildDataSource, selectedAnalyses]);

    const shouldRawRefetchData = useCallback((
        categoryId: string,
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearchTerm?: string
    ) => {
        const currentParams = {
            filters: explicitFilters || rawFilters,
            topItems,
            customTopItems,
            sortBy: explicitSortBy !== undefined ? explicitSortBy : rawSortBy,
            sortOrder: explicitSortOrder !== undefined ? explicitSortOrder : rawSortOrder,
            searchTerm: explicitSearchTerm !== undefined ? explicitSearchTerm : debouncedRawSearchTerm
        };

        const prevParams = rawFetchParamsRef.current.get(categoryId);

        if (!prevParams) return true;

        return prevParams.topItems !== currentParams.topItems ||
            prevParams.customTopItems !== currentParams.customTopItems ||
            prevParams.sortBy !== currentParams.sortBy ||
            prevParams.sortOrder !== currentParams.sortOrder ||
            prevParams.searchTerm !== currentParams.searchTerm ||
            JSON.stringify(prevParams.filters) !== JSON.stringify(currentParams.filters);
    }, [rawFilters, topItems, customTopItems, rawSortBy, rawSortOrder, debouncedRawSearchTerm]);

    const updateRawFetchParams = useCallback((
        categoryId: string,
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearchTerm?: string
    ) => {
        const filtersToUse = explicitFilters || rawFilters;
        const sortToStore = explicitSortBy !== undefined ? explicitSortBy : rawSortBy;
        const orderToStore = explicitSortOrder !== undefined ? explicitSortOrder : rawSortOrder;
        const searchTermToStore = explicitSearchTerm !== undefined ? explicitSearchTerm : debouncedRawSearchTerm;

        rawFetchParamsRef.current.set(categoryId, {
            filters: filtersToUse,
            topItems,
            customTopItems,
            sortBy: sortToStore,
            sortOrder: orderToStore,
            searchTerm: searchTermToStore
        });
    }, [rawFilters, topItems, customTopItems, rawSortBy, rawSortOrder, debouncedRawSearchTerm]);

    const addToRawLoadingCategories = useCallback((categoryId: string) => {
        setRawLoadingCoSoldCategoryIds(prev =>
            prev.includes(categoryId) ? prev : [...prev, categoryId]
        );
    }, []);

    const removeFromRawLoadingCategories = useCallback((categoryId: string) => {
        setRawLoadingCoSoldCategoryIds(prev =>
            prev.filter(id => id !== categoryId)
        );
    }, []);

    const isRawCoSoldCategoryLoading = useCallback((categoryId: string) => {
        return rawLoadingCoSoldCategoryIds.includes(categoryId);
    }, [rawLoadingCoSoldCategoryIds]);

    const addToCuratedLoadingCategories = useCallback((categoryId: string) => {
        setCuratedLoadingCoSoldCategoryIds(prev =>
            prev.includes(categoryId) ? prev : [...prev, categoryId]
        );
    }, []);

    const removeFromCuratedLoadingCategories = useCallback((categoryId: string) => {
        setCuratedLoadingCoSoldCategoryIds(prev =>
            prev.filter(id => id !== categoryId)
        );
    }, []);

    const isCuratedCoSoldCategoryLoading = useCallback((categoryId: string) => {
        return curatedLoadingCoSoldCategoryIds.includes(categoryId);
    }, [curatedLoadingCoSoldCategoryIds]);

    const fetchRawCoSoldCategories = useCallback(async (
        categoryId: string,
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearchTerm?: string
    ) => {
        if (!channelKey) return null;

        try {
            addToRawLoadingCategories(categoryId);

            const filtersToUse = explicitFilters || rawFilters;
            const sortColumn = explicitSortBy !== undefined ? explicitSortBy : rawSortBy;
            const sortDirection = explicitSortOrder !== undefined ? explicitSortOrder : rawSortOrder;
            const searchTerm = explicitSearchTerm !== undefined ? explicitSearchTerm : debouncedRawSearchTerm;
            const limit = topItems === 'custom' ? customTopItems : topItems;

            const response = await fetchCoSoldCategories(categoryId, channelKey, {
                limit,
                filters: filtersToUse,
                is_curated: undefined,
                sort_by: sortColumn,
                sort_order: sortDirection,
                search: searchTerm,
                data_source: buildDataSource(selectedAnalyses),
            });

            updateRawFetchParams(
                categoryId,
                filtersToUse,
                sortColumn,
                sortDirection,
                searchTerm
            );

            if (response?.data) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('Error fetching raw co-sold categories:', error);
            return null;
        } finally {
            removeFromRawLoadingCategories(categoryId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelKey, rawFilters, topItems, customTopItems, rawSortBy, rawSortOrder, updateRawFetchParams, debouncedRawSearchTerm, buildDataSource, selectedAnalyses]);

    const refreshRawExpandedCategories = useCallback(async (
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        forceRefresh: boolean = false,
        explicitSearchTerm?: string
    ) => {
        if (rawExpandedCategories.size === 0) return;

        // setRawLoadingCoSoldCategories(true);

        try {
            const categoriesToRefresh = forceRefresh
                ? Array.from(rawExpandedCategories)
                : Array.from(rawExpandedCategories).filter(categoryId =>
                    shouldRawRefetchData(
                        categoryId,
                        explicitFilters,
                        explicitSortBy,
                        explicitSortOrder,
                        explicitSearchTerm
                    )
                );

            if (categoriesToRefresh.length === 0) {
                return;
            } else {
                categoriesToRefresh.forEach((e) => addToRawLoadingCategories(e));
            }

            const promises = categoriesToRefresh.map(categoryId =>
                fetchRawCoSoldCategories(
                    categoryId,
                    explicitFilters,
                    explicitSortBy,
                    explicitSortOrder,
                    explicitSearchTerm
                )
            );

            const results = await Promise.all(promises);
            const validResults = results.filter(Boolean) as CoSoldCategoryParent[];

            if (validResults.length > 0) {
                setRawCoSoldCategories(prev => {
                    const refreshedIds = new Set(validResults.map(item => item.category_id));
                    const remainingCategories = prev.filter(item => !refreshedIds.has(item.category_id));
                    return [...remainingCategories, ...validResults];
                });
            }
        } catch (error) {
            console.error('Error refreshing co-sold categories', error);
        } finally {
            rawExpandedCategories.forEach((e) => removeFromRawLoadingCategories(e));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawExpandedCategories, shouldRawRefetchData, fetchRawCoSoldCategories]);

    const handleRawSort = useCallback((column: SortOption) => {
        const isSameColumn = column === rawSortBy;
        const newSortOrder = isSameColumn ? (rawSortOrder === 1 ? -1 : 1) : 1;

        if (column === 'category_name' || column === 'category_type_name') {
            fetchRawCategories(undefined, column, newSortOrder);
        }

        if (column !== 'category_name' && column !== 'category_type_name') {
            refreshRawExpandedCategories(undefined, column, newSortOrder, true);
        }

        setRawSortBy(column);
        setRawSortOrder(newSortOrder);
    }, [fetchRawCategories, refreshRawExpandedCategories, rawSortBy, rawSortOrder]);

    const handleRawCategoryExpand = useCallback(async (categoryId: string) => {
        if (rawExpandedCategories.has(categoryId)) {
            setRawCoSoldCategories(prev =>
                prev.filter(item => item.category_id !== categoryId)
            );
            const newExpandedCategories = new Set(rawExpandedCategories);
            newExpandedCategories.delete(categoryId);
            setRawExpandedCategories(newExpandedCategories);
            return;
        }

        const needsRefetch = shouldRawRefetchData(categoryId);
        const alreadyHasData = rawCoSoldCategories.some(cat => cat.category_id === categoryId);

        try {
            addToRawLoadingCategories(categoryId);

            if (needsRefetch || !alreadyHasData) {
                const categoryData = await fetchRawCoSoldCategories(categoryId);

                if (categoryData) {
                    setRawCoSoldCategories(prev => {
                        const exists = prev.some(item => item.category_id === categoryId);
                        if (exists) {
                            return prev.map(item =>
                                item.category_id === categoryId ? categoryData : item
                            );
                        } else {
                            return [...prev, categoryData];
                        }
                    });
                }
            }

            const newExpandedCategories = new Set(rawExpandedCategories);
            newExpandedCategories.add(categoryId);
            setRawExpandedCategories(newExpandedCategories);
        } catch (error) {
            console.error('Error expanding category:', error);
        } finally {
            removeFromRawLoadingCategories(categoryId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawExpandedCategories, rawCoSoldCategories, shouldRawRefetchData, fetchRawCoSoldCategories]);

    const handleRawSearch = useCallback((term: string) => {
        setRawSearchTerm(term);
    }, []);

    const handleRawFilterChange = useCallback((newFilters: FilterOptions) => {
        const isCategoryFilterChanged =
            JSON.stringify(rawFilters.categories) !== JSON.stringify(newFilters.categories) ||
            JSON.stringify(rawFilters.category_types) !== JSON.stringify(newFilters.category_types);

        const isCoSoldFilterChanged =
            JSON.stringify(rawFilters.co_sold_categories) !== JSON.stringify(newFilters.co_sold_categories) ||
            JSON.stringify(rawFilters.co_sold_category_types) !== JSON.stringify(newFilters.co_sold_category_types) ||
            rawFilters.is_curated !== newFilters.is_curated;

        setRawFilters(newFilters);

        if (isCategoryFilterChanged || isCoSoldFilterChanged) {
            setRawLoadingCategories(true);

            fetchCategoriesAndCategoryTypes({
                search: debouncedRawSearchTerm,
                channelKey: channelKey,
                sort_by: rawSortBy,
                sort_order: rawSortOrder,
                filters: newFilters
            }).then(data => {
                if (data?.data) {
                    setRawCategories(data.data.categories);
                    setRawCategoryTypes(data.data.category_types);

                    const newCategoryIds = new Set(data.data.categories.map((cat: Category) => cat.category_id));
                    const validExpandedCategories = new Set(
                        Array.from(rawExpandedCategories)
                            .filter(categoryId => newCategoryIds.has(categoryId))
                    );

                    setRawExpandedCategories(validExpandedCategories);

                    setRawCoSoldCategories(prev =>
                        prev.filter(item => validExpandedCategories.has(item.category_id))
                    );

                    if (validExpandedCategories.size > 0) {
                        refreshRawExpandedCategories(newFilters);
                    }
                }
                setRawLoadingCategories(false);
            }).catch(error => {
                console.error('Error fetching categories with filters:', error);
                setRawLoadingCategories(false);
            });
        }
    }, [
        rawFilters,
        debouncedRawSearchTerm,
        channelKey,
        rawSortBy,
        rawSortOrder,
        rawExpandedCategories,
        refreshRawExpandedCategories
    ]);

    const handleRawRemoveFilter = useCallback((filterType: keyof FilterOptions | 'all', value?: string) => {
        let newFilters: FilterOptions;

        if (filterType === 'all') {
            newFilters = {
                categories: [],
                category_types: [],
                co_sold_categories: [],
                co_sold_category_types: [],
                is_curated: undefined,
                curatedSelections: []
            };
        } else if (filterType === 'is_curated') {
            newFilters = {
                ...rawFilters,
                is_curated: undefined,
                curatedSelections: []
            };
        } else if (value && Array.isArray(rawFilters[filterType])) {
            newFilters = { ...rawFilters };

            if (filterType === 'categories' ||
                filterType === 'category_types' ||
                filterType === 'co_sold_categories' ||
                filterType === 'co_sold_category_types') {

                if (filterType === 'categories' || filterType === 'co_sold_categories') {
                    newFilters[filterType] = rawFilters[filterType].filter((item: Category) =>
                        item.category_id !== value
                    );
                }

                if (filterType === 'category_types' || filterType === 'co_sold_category_types') {
                    newFilters[filterType] = rawFilters[filterType].filter((item: CategoryType) =>
                        item.category_type_id !== value
                    );
                }
            }
        } else {
            return;
        }

        handleRawFilterChange(newFilters);
    }, [rawFilters, handleRawFilterChange]);

    const handleTopItemsChange = useCallback((option: TopItemsOption, customValue?: number) => {
        setTopItems(option);
        if (option === 'custom' && customValue !== undefined) {
            setCustomTopItems(customValue);
        }

        if (rawExpandedCategories.size === 0) return;

        const limit = option === 'custom'
            ? (customValue !== undefined ? customValue : customTopItems)
            : option;


        const categoriesToRefresh = Array.from(rawExpandedCategories);
        categoriesToRefresh.forEach((e) => addToRawLoadingCategories(e));

        Promise.all(
            categoriesToRefresh.map(categoryId =>
                fetchCoSoldCategories(categoryId, channelKey, {
                    limit,
                    filters: rawFilters,
                    is_curated: undefined,
                    sort_by: rawSortBy,
                    sort_order: rawSortOrder,
                    search: debouncedRawSearchTerm
                })
            )
        )
            .then(responses => {
                const validResults = responses
                    .filter(response => response?.data)
                    .map(response => response.data);

                if (validResults.length > 0) {
                    setRawCoSoldCategories(prev => {
                        const refreshedIds = new Set(validResults.map(item => item.category_id));
                        return [
                            ...prev.filter(item => !refreshedIds.has(item.category_id)),
                            ...validResults
                        ];
                    });
                }

                const newCustomValue = option === 'custom' && customValue !== undefined
                    ? customValue
                    : customTopItems;

                categoriesToRefresh.forEach(categoryId => {
                    rawFetchParamsRef.current.set(categoryId, {
                        filters: rawFilters,
                        topItems: option,
                        customTopItems: newCustomValue,
                        sortBy: rawSortBy,
                        sortOrder: rawSortOrder,
                        searchTerm: debouncedRawSearchTerm
                    });
                });
            })
            .catch(error => {
                console.error('Error fetching categories for top items:', error);
            })
            .finally(() => {
                categoriesToRefresh.forEach((e) => removeFromRawLoadingCategories(e));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        rawExpandedCategories,
        channelKey,
        rawFilters,
        rawSortBy,
        rawSortOrder,
        customTopItems,
        debouncedRawSearchTerm
    ]);

    const fetchCuratedCategoriesData = useCallback(async (
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearchTerm?: string
    ) => {
        if (!channelKey) return;

        try {
            setCuratedLoadingCategories(true);

            const baseFilters = explicitFilters || curatedFilters;

            const filtersWithCurated = {
                ...baseFilters,
                is_curated: true
            };

            const sortByToUse = explicitSortBy || curatedSortBy;
            const sortOrderToUse = explicitSortOrder || curatedSortOrder;
            const searchTermToUse = explicitSearchTerm !== undefined ? explicitSearchTerm : debouncedCuratedSearchTerm;

            const response = await fetchCategoriesAndCategoryTypes({
                search: searchTermToUse,
                channelKey: channelKey,
                sort_by: sortByToUse,
                sort_order: sortOrderToUse,
                filters: filtersWithCurated,
            });

            if (response?.data) {
                const formattedData: CoSoldCategoryParent[] = response.data.categories.map((category: Category) => ({
                    category_id: category.category_id,
                    category_name: category.category_name,
                    co_sold_categories: []
                }));

                setCuratedCategories(formattedData);
            } else {
                setCuratedCategories([]);
            }
        } catch (error) {
            console.error('Error fetching curated categories:', error);
        } finally {
            setCuratedLoadingCategories(false);
        }
    }, [channelKey, curatedFilters, curatedSortBy, curatedSortOrder, debouncedCuratedSearchTerm]);

    const updateCuratedFetchParams = useCallback((
        categoryId: string,
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearchTerm?: string
    ) => {
        const filtersToUse = explicitFilters || curatedFilters;
        const sortToStore = explicitSortBy !== undefined ? explicitSortBy : curatedSortBy;
        const orderToStore = explicitSortOrder !== undefined ? explicitSortOrder : curatedSortOrder;
        const searchTermToStore = explicitSearchTerm !== undefined ? explicitSearchTerm : debouncedCuratedSearchTerm;

        curatedFetchParamsRef.current.set(categoryId, {
            filters: filtersToUse,
            sortBy: sortToStore,
            sortOrder: orderToStore,
            searchTerm: searchTermToStore
        });
    }, [curatedFilters, curatedSortBy, curatedSortOrder, debouncedCuratedSearchTerm]);

    const fetchCuratedCoSoldCategories = useCallback(async (
        categoryId: string,
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearchTerm?: string
    ) => {
        if (!channelKey) return null;

        try {
            addToCuratedLoadingCategories(categoryId);

            const baseFilters = explicitFilters || curatedFilters;

            const filtersWithCurated = {
                ...baseFilters,
                is_curated: true
            };

            const sortColumn = explicitSortBy !== undefined ? explicitSortBy : curatedSortBy;
            const sortDirection = explicitSortOrder !== undefined ? explicitSortOrder : curatedSortOrder;
            const searchTerm = explicitSearchTerm !== undefined ? explicitSearchTerm : debouncedCuratedSearchTerm;

            const limit = masterCategories.length || 100;

            const response = await fetchCoSoldCategories(categoryId, channelKey, {
                limit,
                filters: filtersWithCurated,
                is_curated: true,
                sort_by: sortColumn,
                sort_order: sortDirection,
                search: searchTerm,
            });

            updateCuratedFetchParams(
                categoryId,
                baseFilters,
                sortColumn,
                sortDirection,
                searchTerm
            );

            if (response?.data) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('Error fetching curated co-sold categories:', error);
            return null;
        } finally {
            removeFromCuratedLoadingCategories(categoryId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelKey, curatedFilters, curatedSortBy, curatedSortOrder, masterCategories.length, debouncedCuratedSearchTerm, updateCuratedFetchParams]);

    const shouldCuratedRefetchData = useCallback((
        categoryId: string,
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearchTerm?: string
    ) => {
        const currentParams = {
            filters: explicitFilters || curatedFilters,
            sortBy: explicitSortBy !== undefined ? explicitSortBy : curatedSortBy,
            sortOrder: explicitSortOrder !== undefined ? explicitSortOrder : curatedSortOrder,
            searchTerm: explicitSearchTerm !== undefined ? explicitSearchTerm : debouncedCuratedSearchTerm
        };

        const prevParams = curatedFetchParamsRef.current.get(categoryId);

        if (!prevParams) return true;

        return prevParams.sortBy !== currentParams.sortBy ||
            prevParams.sortOrder !== currentParams.sortOrder ||
            prevParams.searchTerm !== currentParams.searchTerm ||
            JSON.stringify(prevParams.filters) !== JSON.stringify(currentParams.filters);
    }, [curatedFilters, curatedSortBy, curatedSortOrder, debouncedCuratedSearchTerm]);

    const refreshCuratedExpandedCategories = useCallback(async (
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        forceRefresh: boolean = false,
        explicitSearchTerm?: string
    ) => {
        if (curatedExpandedCategories.size === 0) return;

        try {
            const categoriesToRefresh = forceRefresh
                ? Array.from(curatedExpandedCategories)
                : Array.from(curatedExpandedCategories).filter(categoryId =>
                    shouldCuratedRefetchData(
                        categoryId,
                        explicitFilters,
                        explicitSortBy,
                        explicitSortOrder,
                        explicitSearchTerm
                    )
                );

            if (categoriesToRefresh.length === 0) {
                return;
            } else {
                categoriesToRefresh.forEach((e) => addToCuratedLoadingCategories(e));
            }

            const promises = categoriesToRefresh.map(categoryId =>
                fetchCuratedCoSoldCategories(
                    categoryId,
                    explicitFilters,
                    explicitSortBy,
                    explicitSortOrder,
                    explicitSearchTerm
                )
            );

            const results = await Promise.all(promises);
            const validResults = results.filter(Boolean) as CoSoldCategoryParent[];

            if (validResults.length > 0) {
                setCuratedCoSoldCategories(prev => {
                    const refreshedIds = new Set(validResults.map(item => item.category_id));
                    const remainingCategories = prev.filter(item => !refreshedIds.has(item.category_id));
                    return [...remainingCategories, ...validResults];
                });
            }
        } catch (error) {
            console.error('Error refreshing curated co-sold categories:', error);
        } finally {
            curatedExpandedCategories.forEach((e) => removeFromCuratedLoadingCategories(e));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [curatedExpandedCategories, shouldCuratedRefetchData, fetchCuratedCoSoldCategories]);

    const handleCuratedSort = useCallback((column: SortOption) => {
        const isSameColumn = column === curatedSortBy;
        const newSortOrder = isSameColumn ? (curatedSortOrder === 1 ? -1 : 1) : 1;

        if (column === 'category_name' || column === 'category_type_name') {
            fetchCuratedCategoriesData(undefined, column, newSortOrder);
        }

        if (column !== 'category_name' && column !== 'category_type_name') {
            refreshCuratedExpandedCategories(undefined, column, newSortOrder, true);
        }

        setCuratedSortBy(column);
        setCuratedSortOrder(newSortOrder);
    }, [fetchCuratedCategoriesData, refreshCuratedExpandedCategories, curatedSortBy, curatedSortOrder]);

    const handleCuratedCategoryExpand = useCallback(async (categoryId: string) => {
        if (curatedExpandedCategories.has(categoryId)) {
            setCuratedCoSoldCategories(prev =>
                prev.filter(item => item.category_id !== categoryId)
            );

            const newExpandedCategories = new Set(curatedExpandedCategories);
            newExpandedCategories.delete(categoryId);
            setCuratedExpandedCategories(newExpandedCategories);

            return;
        }

        const needsRefetch = shouldCuratedRefetchData(categoryId);
        const alreadyHasData = curatedCoSoldCategories.some(cat => cat.category_id === categoryId);

        try {

            if (needsRefetch || !alreadyHasData) {
                addToCuratedLoadingCategories(categoryId);
                const categoryData = await fetchCuratedCoSoldCategories(categoryId);

                if (categoryData) {
                    setCuratedCoSoldCategories(prev => {
                        const exists = prev.some(item => item.category_id === categoryId);
                        if (exists) {
                            return prev.map(item =>
                                item.category_id === categoryId ? categoryData : item
                            );
                        } else {
                            return [...prev, categoryData];
                        }
                    });
                }
            }

            const newExpandedCategories = new Set(curatedExpandedCategories);
            newExpandedCategories.add(categoryId);
            setCuratedExpandedCategories(newExpandedCategories);
        } catch (error) {
            console.error('Error expanding curated category:', error);
        } finally {
            removeFromCuratedLoadingCategories(categoryId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [curatedExpandedCategories, curatedCoSoldCategories, shouldCuratedRefetchData, fetchCuratedCoSoldCategories]);

    const handleCuratedSearch = useCallback((term: string) => {
        setCuratedSearchTerm(term);
    }, []);

    const handleCuratedFilterChange = useCallback((newFilters: FilterOptions) => {
        setCuratedFilters(newFilters);
        fetchCuratedCategoriesData(newFilters);

        if (curatedExpandedCategories.size > 0) {
            refreshCuratedExpandedCategories(newFilters);
        }
    }, [fetchCuratedCategoriesData, refreshCuratedExpandedCategories, curatedExpandedCategories]);

    const handleCuratedRemoveFilter = useCallback((filterType: keyof FilterOptions | 'all', value?: string) => {
        let newFilters: FilterOptions;

        if (filterType === 'all') {
            newFilters = {
                categories: [],
                category_types: [],
                co_sold_categories: [],
                co_sold_category_types: [],
                is_curated: undefined,
                curatedSelections: []
            };
        } else if (filterType === 'is_curated') {
            newFilters = {
                ...curatedFilters,
                is_curated: undefined,
                curatedSelections: []
            };
        } else if (value && Array.isArray(curatedFilters[filterType])) {
            newFilters = { ...curatedFilters };

            if (filterType === 'categories' ||
                filterType === 'category_types' ||
                filterType === 'co_sold_categories' ||
                filterType === 'co_sold_category_types') {

                if (filterType === 'categories' || filterType === 'co_sold_categories') {
                    newFilters[filterType] = curatedFilters[filterType].filter((item: Category) =>
                        item.category_id !== value
                    );
                }

                if (filterType === 'category_types' || filterType === 'co_sold_category_types') {
                    newFilters[filterType] = curatedFilters[filterType].filter((item: CategoryType) =>
                        item.category_type_id !== value
                    );
                }
            }
        } else {
            return;
        }

        handleCuratedFilterChange(newFilters);
    }, [curatedFilters, handleCuratedFilterChange]);

    const handleToggleCuration = useCallback(async (
        coSoldCategoryId: string,
        parentCategoryId: string,
        isCurated: boolean,
        reviewStatus: CoSoldCategory['review_status']
    ) => {
        if (!channelKey) return;
        if (!updatePermission) { toast.error("Permission denied."); return }
        // A pair must be Reviewed before it can be curated (un-curating stays allowed so an
        // already-curated pending pair isn't trapped). The toggles are disabled for this case
        // too — this guard backstops any other caller.
        if (isCurated && reviewStatus !== 'Reviewed') {
            toast.error("This category pair is pending review. Mark it as Reviewed before curating.");
            return;
        }

        const updateCurationInList = (setter: React.Dispatch<React.SetStateAction<CoSoldCategoryParent[]>>, newIsCurated: boolean) => {
            setter(prev =>
                prev.map(category =>
                    category.category_id === parentCategoryId
                        ? {
                            ...category,
                            co_sold_categories: category.co_sold_categories.map(item =>
                                item.category_id === coSoldCategoryId
                                    ? { ...item, is_curated: newIsCurated }
                                    : item
                            )
                        }
                        : category
                )
            );
        };

        const updateCurationState = (newIsCurated: boolean) => {
            updateCurationInList(setRawCoSoldCategories, newIsCurated);
            updateCurationInList(setTop25Rows, newIsCurated);
        };

        updateCurationState(isCurated);

        try {
            const response = await toggleCuration(channelKey, parentCategoryId, coSoldCategoryId, isCurated);

            if (response?.done !== true) {
                updateCurationState(!isCurated);
                toast.error("Failed to update curation status");
            }
            if (activeTab === 'curated') {
                fetchCuratedCategoriesData();
            }
        } catch (error) {
            console.error('Error toggling curation:', error);
            const errorMessage = error instanceof Error
                ? error.message
                : 'Failed to update curation status';

            toast.error(errorMessage);
            updateCurationState(!isCurated);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, fetchCuratedCategoriesData, channelKey]);

    const handleUpdateReviewStatus = useCallback(async (
        coSoldCategoryId: string,
        parentCategoryId: string
    ) => {
        if (!channelKey) return;
        if (!updatePermission) { toast.error("Permission denied."); return; }

        const updateReviewStatusInList = (setter: React.Dispatch<React.SetStateAction<CoSoldCategoryParent[]>>, review_status: "Reviewed" | "Review Pending") => {
            setter(prev =>
                prev.map(category =>
                    category.category_id === parentCategoryId
                        ? {
                            ...category,
                            co_sold_categories: category.co_sold_categories.map(item =>
                                item.category_id === coSoldCategoryId
                                    ? { ...item, review_status }
                                    : item
                            )
                        }
                        : category
                )
            );
        };

        const setCoSoldReviewStatus = (review_status: "Reviewed" | "Review Pending") => {
            updateReviewStatusInList(setRawCoSoldCategories, review_status);
            updateReviewStatusInList(setTop25Rows, review_status);
        };

        const adjustReviewPendingCount = (delta: number) => {
            setRawCategories(prev =>
                prev?.map(cat =>
                    cat.category_id === parentCategoryId
                        ? { ...cat, under_review_count: Math.max(0, (cat.under_review_count ?? 0) + delta) }
                        : cat
                )
            );
        };

        const parentEntry = rawCoSoldCategories.find(c => c.category_id === parentCategoryId)
            || top25Rows.find(c => c.category_id === parentCategoryId);
        const coSoldEntry = parentEntry?.co_sold_categories.find(cs => cs.category_id === coSoldCategoryId);
        const isCurated = !!coSoldEntry?.is_curated;

        // Optimistic update
        setCoSoldReviewStatus("Reviewed");
        adjustReviewPendingCount(-1);

        try {
            const response = await updateReviewStatus(channelKey, parentCategoryId, coSoldCategoryId, isCurated);

            if (response?.done !== true) {
                setCoSoldReviewStatus("Review Pending");
                adjustReviewPendingCount(1);
                toast.error("Failed to update review status");
            }
        } catch (error) {
            console.error('Error updating review status:', error);
            setCoSoldReviewStatus("Review Pending");
            adjustReviewPendingCount(1);
            toast.error("Failed to update review status");
        }
    }, [channelKey, updatePermission, rawCoSoldCategories, top25Rows]);

    const handleRowDensityChange = useCallback((density: RowDensity) => {
        setRowDensity(density);
    }, []);

    const handleTabChange = useCallback((tab: TabType) => {
        setOpenMenu(undefined);

        if (tab === activeTab) return;

        setActiveTab(tab);

        if (tab === 'curated') {
            fetchCuratedCategoriesData();
            if (!curatedInitialized.current) {
                curatedInitialized.current = true;
            }

            if (curatedExpandedCategories.size > 0) {
                const expandedCategoryIds = Array.from(curatedExpandedCategories);
                curatedExpandedCategories.forEach((e) => addToCuratedLoadingCategories(e));
                Promise.all(
                    expandedCategoryIds.map(categoryId =>
                        fetchCuratedCoSoldCategories(categoryId)
                    )
                )
                    .then(results => {
                        const validResults = results.filter(Boolean) as CoSoldCategoryParent[];
                        if (validResults.length > 0) {
                            setCuratedCoSoldCategories(validResults);
                        }
                    })
                    .catch(error => {
                        console.error('Error loading curated co-sold categories:', error);
                    })
                    .finally(() => {
                        curatedExpandedCategories.forEach((e) => removeFromCuratedLoadingCategories(e));
                    });
            }
        } else if (tab === 'top25') {
            if (!top25Initialized.current) {
                top25Initialized.current = true;
            }
            fetchTop25Data();
        } else {
            // 'all' tab - always refresh on every visit
            rawInitialized.current = true;
            fetchRawCategories();

            if (rawExpandedCategories.size > 0) {
                refreshRawExpandedCategories(undefined, undefined, undefined, true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        activeTab,
        fetchRawCategories,
        fetchCuratedCategoriesData,
        fetchCuratedCoSoldCategories,
        fetchTop25Data,
        refreshRawExpandedCategories,
        curatedExpandedCategories,
        rawExpandedCategories
    ]);

    const getRawCoSoldCategoriesForId = useCallback(
        (categoryId: string): CoSoldCategory[] => {
            const parent = rawCoSoldCategories.find(item => item.category_id === categoryId);
            return parent?.co_sold_categories || [];
        },
        [rawCoSoldCategories]
    );

    const getCuratedCoSoldCategoriesForId = useCallback(
        (categoryId: string): CoSoldCategory[] => {
            const parent = curatedCoSoldCategories.find(item => item.category_id === categoryId);
            return parent?.co_sold_categories || [];
        },
        [curatedCoSoldCategories]
    );

    const checkComputeStatus = useCallback(async (key: string) => {
        try {
            const data = await getComputeStatus(key);
            const status = data?.data?.status;
            const lastComputedAt = data?.data?.last_computed_at;
            const wasComputing = prevIsComputingRef.current;
            const prevLastComputedAt = lastComputedAtRef.current;

            // Detect "compute completed" by `last_computed_at` advancing past
            // what we last saw. This is strictly stronger than the previous
            // Computing→Computed transition check because:
            //   (a) it still fires for the common case (Computing → Computed),
            //       since a successful compute always bumps last_computed_at
            //       via RelevancyEngine._update_meta, and
            //   (b) it ALSO catches the case where the compute starts and
            //       finishes between two of our polls — e.g. a fast
            //       weight_rescore, or a page reload happening as another
            //       session's compute is wrapping up. The old approach
            //       missed those because it required observing the
            //       Computing state at least once.
            // The very first observation (prevLastComputedAt === null) is
            // treated as a baseline, NOT an advancement, so the initial
            // page load doesn't immediately refetch.
            if (prevLastComputedAt != null
                && lastComputedAt != null
                && lastComputedAt > prevLastComputedAt) {
                setPostComputeRefetchTrigger(c => c + 1);
                // Definitive completion observed — drop the optimistic sticky.
                userInitiatedComputeAtRef.current = null;
            }
            if (lastComputedAt != null) {
                lastComputedAtRef.current = lastComputedAt;
            }

            if (status === 'Computing') {
                // ARS confirmed it picked up the trigger — drop the sticky and
                // let the normal polling logic own isComputing from here on.
                userInitiatedComputeAtRef.current = null;
                setIsComputing(true);
                prevIsComputingRef.current = true;
                const logs = data?.data?.processing_logs;
                if (logs && logs.length > 0 && logs[0].created_at) {
                    setComputeStartedAt(logs[0].created_at * 1000);
                }
            } else {
                // Sticky window: a user-initiated compute (manual Compute or
                // weightage save) was fired within the last
                // USER_INITIATED_COMPUTE_STICKY_MS but ARS hasn't reported
                // `Computing` yet. Hold isComputing=true so the polling
                // effect stays at the 4s cadence — otherwise the next poll
                // would re-arm at 10min and the user would sit on stale
                // data until the slow tick.
                const stickyAt = userInitiatedComputeAtRef.current;
                if (stickyAt != null && (Date.now() - stickyAt) < USER_INITIATED_COMPUTE_STICKY_MS) {
                    return;
                }
                userInitiatedComputeAtRef.current = null;

                // Surface failure outcomes. The ARS engine writes
                // `status: "Error Occurred"` to the compute log on raise
                // (see relevancy_engine.RelevancyEngine.compute's except
                // branch). Today the BE's get_compute_status normalises
                // every non-running state to "Computed" and drops the log
                // detail, so this toast is dormant until the BE is updated
                // to propagate the failure status; the FE is wired so the
                // user gets a notification the moment that ships, instead
                // of silently seeing the spinner disappear.
                if (wasComputing && (status === 'Failed' || status === 'Error Occurred')) {
                    toast.error('Category relevancy compute failed. Check the compute log for details.');
                }
                setIsComputing(false);
                prevIsComputingRef.current = false;
                setComputeStartedAt(null);
                if (lastComputedAt != null) {
                    setLastUpdatedOn(Math.round(lastComputedAt * 1000));
                }
            }
        } catch {
            // silently ignore polling errors
        }
    }, []);

    const handleComputeTrigger = useCallback(async () => {
        if (!channelKey) return;
        // Mark the sticky window so the first poll-after-trigger doesn't bounce
        // isComputing back to false during the Celery→ARS handoff gap.
        userInitiatedComputeAtRef.current = Date.now();
        setIsComputing(true);
        setComputeStartedAt(Date.now());
        try {
            await triggerFullCompute(channelKey);
        } catch (error) {
            userInitiatedComputeAtRef.current = null;
            setIsComputing(false);
            setComputeStartedAt(null);
            throw error;
        }
    }, [channelKey]);

    // Called by `ManageUnifiedScoringPanel` after the PUT weightage_config
    // returns successfully. The BE already kicked off the `weight_rescore`
    // Celery → ARS chain on its side; this just lets the FE know to flip
    // into the "compute in progress" state so the user sees the banner,
    // polling re-arms at 4s, and `last_computed_at` advancement fires
    // table refetches the moment the rescore lands.
    const notifyWeightageRescoreTriggered = useCallback(() => {
        if (!channelKey) return;
        userInitiatedComputeAtRef.current = Date.now();
        setIsComputing(true);
        setComputeStartedAt(Date.now());
        // Run an immediate status check so we don't wait up to 4s for the
        // first poll tick — the effect's deps include isComputing so the
        // interval re-arms, but the immediate call here covers the gap.
        checkComputeStatus(channelKey);
    }, [channelKey, checkComputeStatus]);

    useEffect(() => {
        if (channelKey && !masterListsLoaded.current) {
            if (activeTab === 'all') {
                // fetchRawCategories also populates masterCategories on first load
                if (!rawInitialized.current) {
                    fetchRawCategories();
                    rawInitialized.current = true;
                }
            } else if (activeTab === 'top25') {
                // fetchTop25Data also populates masterCategories on first load
                if (!top25Initialized.current) {
                    fetchTop25Data();
                    top25Initialized.current = true;
                }
            } else if (activeTab === 'curated') {
                loadMasterLists().then(() => {
                    if (!curatedInitialized.current) {
                        fetchCuratedCategoriesData();
                        curatedInitialized.current = true;
                    }
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelKey, activeTab, fetchRawCategories, fetchCuratedCategoriesData, loadMasterLists]);

    // Compute-status poll. Adaptive interval: 4s while a compute is actively
    // running so the user sees completion quickly, and 10min while idle to keep
    // detecting computes triggered by another user/session without hammering
    // the BE. The effect re-runs on isComputing flip — old interval is cleared,
    // an immediate check fires, and a new interval is armed at the right
    // cadence. Banner stays a pure function of the latest poll result, so it
    // appears/disappears correctly whether the user just clicked Compute or
    // navigated back to the page mid-compute.
    useEffect(() => {
        if (!channelKey) return;
        checkComputeStatus(channelKey);
        const intervalMs = isComputing ? 4_000 : 10 * 60 * 1000;
        const id = setInterval(() => checkComputeStatus(channelKey), intervalMs);
        return () => clearInterval(id);
    }, [channelKey, isComputing, checkComputeStatus]);

    // Refresh table data when a compute completes. Triggered by
    // `checkComputeStatus` bumping `postComputeRefetchTrigger` once it
    // observes `last_computed_at` advancing past the previous value. This
    // covers both the normal Computing→Computed transition and the
    // missed-Computing case (fast computes / page reloaded mid-compute).
    // On failure, last_computed_at is NOT advanced (RelevancyEngine doesn't
    // call _update_meta when raising), so this effect correctly does not
    // refetch unchanged data — the failure toast in checkComputeStatus is
    // the user-facing signal there.
    useEffect(() => {
        if (postComputeRefetchTrigger === 0) return;
        if (!channelKey) return;
        fetchRawCategories();
        fetchCuratedCategoriesData();
        fetchTop25Data();
        // Parent-list fetches above don't carry the per-pair unified_score —
        // those live in the co-sold rows fetched per expanded parent. Without
        // forcing a co-sold refetch here, an already-expanded parent on the
        // All / Curated tabs keeps its pre-compute scores until the user
        // collapses+re-expands or switches tabs (which `handleTabChange`
        // handles for 'all'). forceRefresh=true bypasses the param-equality
        // shortcut in `shouldRawRefetchData` so the rescore is reflected
        // even when filters/sort/search haven't changed.
        refreshRawExpandedCategories(undefined, undefined, undefined, true);
        refreshCuratedExpandedCategories(undefined, undefined, undefined, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postComputeRefetchTrigger, channelKey]);

    useEffect(() => {
        if (!channelKey || !masterListsLoaded.current) return;

        if (activeTab === 'curated' && !curatedInitialized.current) {
            fetchCuratedCategoriesData();
            curatedInitialized.current = true;
        } else if (activeTab === 'top25' && !top25Initialized.current) {
            fetchTop25Data();
            top25Initialized.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelKey, activeTab, fetchRawCategories, fetchCuratedCategoriesData, fetchTop25Data, masterListsLoaded.current]);

    const prevRawSearchTermRef = useRef<string>('');

    const prevCuratedSearchTermRef = useRef<string>('');

    const prevTop25SearchTermRef = useRef<string>('');

    useEffect(() => {
        const searchTermChanged = prevRawSearchTermRef.current !== debouncedRawSearchTerm;

        if (activeTab === 'all' && rawInitialized.current && searchTermChanged) {
            fetchRawCategories();

            if (rawExpandedCategories.size > 0) {
                refreshRawExpandedCategories(
                    undefined,
                    undefined,
                    undefined,
                    true,
                    debouncedRawSearchTerm
                );
            }

            prevRawSearchTermRef.current = debouncedRawSearchTerm;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedRawSearchTerm, fetchRawCategories, refreshRawExpandedCategories, activeTab, rawInitialized]);

    useEffect(() => {
        const searchTermChanged = prevCuratedSearchTermRef.current !== debouncedCuratedSearchTerm;

        if (activeTab === 'curated' && curatedInitialized.current && searchTermChanged) {
            fetchCuratedCategoriesData();

            if (curatedExpandedCategories.size > 0) {
                refreshCuratedExpandedCategories(
                    undefined,
                    undefined,
                    undefined,
                    true,
                    debouncedCuratedSearchTerm
                );
            }

            prevCuratedSearchTermRef.current = debouncedCuratedSearchTerm;
        }
    }, [debouncedCuratedSearchTerm, fetchCuratedCategoriesData, refreshCuratedExpandedCategories, activeTab, curatedInitialized, curatedExpandedCategories.size]);

    useEffect(() => {
        const searchTermChanged = prevTop25SearchTermRef.current !== debouncedTop25SearchTerm;

        if (activeTab === 'top25' && top25Initialized.current && searchTermChanged) {
            fetchTop25Data(undefined, undefined, debouncedTop25SearchTerm);
            prevTop25SearchTermRef.current = debouncedTop25SearchTerm;
        }
    }, [debouncedTop25SearchTerm, fetchTop25Data, activeTab]);

    // Re-fetch when analysis selection changes (skip initial mount)
    useEffect(() => {
        if (!analysisEffectMounted.current) {
            analysisEffectMounted.current = true;
            return;
        }
        if (!rawInitialized.current) return;
        if (activeTab === 'all') {
            fetchRawCategories();
            if (rawExpandedCategories.size > 0) {
                refreshRawExpandedCategories(undefined, undefined, undefined, true);
            }
        } else if (activeTab === 'curated') {
            fetchCuratedCategoriesData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAnalyses]);

    // When the Top-25 analysis selection changes: snap top25SortBy to that
    // bucket's natural metric (Order Data → invoice_count, Taxonomy →
    // attribute_overlap, Unified → unified_score) and re-fetch with that
    // sort. Pass natural/-1 to fetchTop25Data explicitly because the
    // just-issued setState hasn't landed in the closure yet — without the
    // explicit args the fetch would close over the previous sort. The BE
    // applies a cascade *selection* metric (is_curated DESC, unified_score
    // DESC, invoice_count DESC, attribute_overlap DESC) on the top-N, so
    // these sort_by values control the display order over the cascade-picked
    // rows. Column-header clicks still win until the user switches buckets.
    useEffect(() => {
        let natural: SortOption = 'invoice_count';
        if (top25IsUnifiedAnalysis) {
            natural = 'unified_score';
        } else if (top25SelectedAnalyses.length === 1 && top25SelectedAnalyses[0] === 'taxonomy') {
            natural = 'attribute_overlap';
        }
        setTop25SortBy(natural);
        setTop25SortOrder(-1);
        if (top25Initialized.current && activeTab === 'top25') {
            fetchTop25Data(natural, -1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [top25SelectedAnalyses, top25IsUnifiedAnalysis]);

    const contextValue = useMemo(() => ({
        activeTab,
        masterCategories,
        masterCategoryTypes,
        rowDensity,
        industryId,
        openMenu,
        showTabSelector,
        rawCategories,
        rawCategoryTypes,
        rawExpandedCategories,
        rawCoSoldCategories,
        rawFilters,
        topItems,
        customTopItems,
        rawSearchTerm,
        rawSearchPending,
        rawSortBy,
        rawSortOrder,
        rawLoadingCategories,
        curatedCategories,
        curatedExpandedCategories,
        curatedCoSoldCategories,
        curatedFilters,
        curatedSearchTerm,
        curatedSearchPending,
        curatedSortBy,
        curatedSortOrder,
        curatedLoadingCategories,
        isRawCoSoldCategoryLoading,
        isCuratedCoSoldCategoryLoading,
        setActiveTab: handleTabChange,
        handleRowDensityChange,
        setOpenMenu,
        handleToggleCuration,
        handleRawCategoryExpand,
        handleRawSearch,
        handleRawFilterChange,
        handleTopItemsChange,
        handleRawRemoveFilter,
        handleRawSort,
        getRawCoSoldCategoriesForId,
        handleCuratedCategoryExpand,
        handleCuratedSearch,
        handleCuratedFilterChange,
        handleCuratedRemoveFilter,
        handleCuratedSort,
        getCuratedCoSoldCategoriesForId,
        lastUpdatedOn,
        readPermission,
        updatePermission,
        handleUpdateReviewStatus,
        selectedAnalyses,
        isUnifiedAnalysis,
        handleAnalysisChange,
        top25SelectedAnalyses,
        top25IsUnifiedAnalysis,
        top25Rows,
        top25Loading,
        top25SortBy,
        top25SortOrder,
        top25SearchTerm,
        top25SearchPending,
        top25Filters,
        handleTop25Search,
        handleTop25AnalysisChange,
        handleTop25Sort,
        handleTop25FilterChange,
        handleTop25RemoveFilter,
        isComputing,
        computeStartedAt,
        handleComputeTrigger,
        notifyWeightageRescoreTriggered,
    }), [
        activeTab, masterCategories, masterCategoryTypes, rowDensity, industryId, openMenu, showTabSelector, isComputing, computeStartedAt, handleComputeTrigger, notifyWeightageRescoreTriggered,
        rawCategories, rawCategoryTypes, rawExpandedCategories, rawCoSoldCategories, rawFilters,
        topItems, customTopItems, rawSearchTerm, rawSortBy, rawSortOrder, rawLoadingCategories,
        curatedCategories, curatedExpandedCategories, curatedCoSoldCategories,
        curatedFilters, curatedSearchTerm, curatedSortBy, curatedSortOrder, curatedLoadingCategories,
        isRawCoSoldCategoryLoading, isCuratedCoSoldCategoryLoading, handleTabChange, handleRowDensityChange, handleToggleCuration,
        handleRawCategoryExpand, handleRawSearch, handleRawFilterChange, handleTopItemsChange,
        handleRawRemoveFilter, handleRawSort, getRawCoSoldCategoriesForId, handleCuratedCategoryExpand,
        handleCuratedSearch, handleCuratedFilterChange, handleCuratedRemoveFilter, handleCuratedSort,
        getCuratedCoSoldCategoriesForId, lastUpdatedOn, readPermission, updatePermission, handleUpdateReviewStatus,
        selectedAnalyses, isUnifiedAnalysis, handleAnalysisChange,
        top25SelectedAnalyses, top25IsUnifiedAnalysis, top25Rows, top25Loading,
        top25SortBy, top25SortOrder, top25SearchTerm, top25Filters,
        rawSearchPending, curatedSearchPending, top25SearchPending,
        handleTop25Search, handleTop25AnalysisChange, handleTop25Sort, handleTop25FilterChange, handleTop25RemoveFilter,
    ]);

    return (
        <CategoryRelevancyContext.Provider value={contextValue}>
            {children}
        </CategoryRelevancyContext.Provider>
    );
};